// src/hooks/useSameDayShipping.js
//
// Hook para "envío el mismo día tipo MELI".
// - Sólo consulta la API si el usuario está logueado (state.user.id).
// - Comparte una única llamada entre todas las instancias del hook (una por página/contexto),
//   evitando que cada ProductCard dispare su propia petición.
// - Refresca periódicamente para cruzar el horario de corte (today -> tomorrow) mientras se navega.
//
// Sobre `cutoff_time`: el backend ya no usa un único horario de corte por zona, sino uno
// POR DÍA (cada día habilitado de la zona tiene el suyo, con el de la zona como respaldo).
// La respuesta mantiene los mismos campos, así que este hook no cambia: sólo hay que tener
// presente que el valor corresponde al corte del día en que se despacha, no a uno fijo.

import { useState, useEffect, useContext } from 'react';
import { MyContext } from '../context/AppContext';
import { getSameDayShippingInfo } from '../services/front';

const HIDDEN = { show_message: false };
const REFRESH_MS = 60 * 1000; // 60s

// Estado compartido a nivel de módulo (singleton) para no repetir la llamada por cada card.
let sharedUserId = null;
let sharedData = HIDDEN;
let inflight = null;
let intervalId = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((cb) => cb(sharedData));
}

function refresh(userId) {
  if (!userId || inflight) return inflight;
  inflight = getSameDayShippingInfo(userId)
    .then((data) => {
      // Ignorar respuestas de un usuario que ya cambió.
      if (userId === sharedUserId) {
        sharedData = data && typeof data.show_message === 'boolean' ? data : HIDDEN;
        notify();
      }
      return sharedData;
    })
    .catch(() => {
      if (userId === sharedUserId) {
        sharedData = HIDDEN;
        notify();
      }
      return HIDDEN;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function ensureForUser(userId) {
  if (userId === sharedUserId) return;

  sharedUserId = userId;
  sharedData = HIDDEN;

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }

  if (userId) {
    refresh(userId);
    intervalId = setInterval(() => refresh(userId), REFRESH_MS);
  } else {
    notify();
  }
}

/**
 * @returns {{show_message:boolean, message?:string, cutoff_time?:string, delivery_type?:string, estimated_date?:string}}
 */
export default function useSameDayShipping() {
  const { state } = useContext(MyContext);
  const userId = state?.user?.id ?? null;
  const [data, setData] = useState(userId ? sharedData : HIDDEN);

  useEffect(() => {
    if (!userId) {
      setData(HIDDEN);
      return undefined;
    }

    ensureForUser(userId);
    setData(sharedData);

    const cb = (d) => setData(d);
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }, [userId]);

  return userId ? data : HIDDEN;
}
